import { Head, router, usePage } from '@inertiajs/react';
import { useMemo, useState } from 'react';

import {
  INFRACTION_LABELS,
  STATUS_COLORS,
  STATUS_LABELS,
  formatReportDate,
  isOverdueAcknowledgement,
  type WhistleblowReport,
} from '@/components/admin/whistleblow/report';
import { FilterTabs } from '@/components/ui/FilterTabs';

interface PageProps {
  reports: WhistleblowReport[];
  [key: string]: unknown;
}

const STATUS_TABS = [
  { label: 'Todas', value: 'all' },
  { label: 'Pendentes', value: 'pending' },
  { label: 'Receção acusada', value: 'acknowledged' },
  { label: 'Em análise', value: 'in_progress' },
  { label: 'Concluídas', value: 'closed' },
];

export default function WhistleblowReports() {
  const { reports } = usePage<PageProps>().props;
  const [statusFilter, setStatusFilter] = useState('pending');

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return reports;
    return reports.filter((r) => r.status === statusFilter);
  }, [reports, statusFilter]);

  return (
    <>
      <Head title="Denúncias — Administração" />
      <div className="space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h1 className="font-headline text-on-surface text-2xl font-bold">Denúncias</h1>
          <p className="text-on-surface-light text-xs">
            Canal de Denúncias · Lei n.º 93/2021 · Conservação nos termos legais
          </p>
        </div>

        <FilterTabs options={STATUS_TABS} value={statusFilter} onChange={setStatusFilter} />

        <div className="surface-card overflow-hidden">
          {filtered.length === 0 ? (
            <div className="text-on-surface-light flex flex-col items-center gap-2 py-12 text-center text-sm">
              <span className="material-symbols-outlined text-on-surface-light/40 text-3xl">
                gavel
              </span>
              Sem denúncias registadas{statusFilter !== 'all' ? ' neste filtro' : ''}.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] border-collapse text-left">
                <thead>
                  <tr className="border-line border-b">
                    {['Referência', 'Tipo', 'Estado', 'Data'].map((h) => (
                      <th
                        key={h}
                        className="text-on-surface-light/70 px-4 py-3 text-xs font-bold tracking-wider uppercase"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((report) => {
                    const overdue = isOverdueAcknowledgement(report);
                    return (
                      <tr
                        key={report.id}
                        onClick={() => router.visit(`/whistleblow-reports/${report.id}`)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            router.visit(`/whistleblow-reports/${report.id}`);
                          }
                        }}
                        tabIndex={0}
                        aria-label={`Abrir denúncia ${report.referenceCode}`}
                        className="border-line hover:bg-background focus:bg-background cursor-pointer border-b transition-colors last:border-b-0 focus:outline-none"
                      >
                        <td className="text-on-surface px-4 py-3.5 font-mono text-sm font-bold">
                          {report.referenceCode}
                        </td>
                        <td className="text-on-surface-light px-4 py-3.5 text-sm">
                          {INFRACTION_LABELS[report.infractionType] ?? report.infractionType}
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="flex flex-wrap items-center gap-2">
                            <span
                              className={`rounded-full border px-2.5 py-0.5 text-[10px] font-bold tracking-wider uppercase ${STATUS_COLORS[report.status]}`}
                            >
                              {STATUS_LABELS[report.status]}
                            </span>
                            {overdue && (
                              <span className="border-error/30 bg-error/10 text-error rounded-full border px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase">
                                Prazo de acusação de receção excedido
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="text-on-surface-light px-4 py-3.5 text-xs">
                          {formatReportDate(report.submittedAt)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
